import {
  EmailReceiver,
  EnrollmentPeriod,
  FinalBrowsingCriteria,
  InputProposalV2,
  InitiateType,
  ProposalAction,
  RFQChecklist,
  SaveProposalInput,
  USER_ACTIONS,
  InputRFQCarrierByPLine,
  DashboardEnrollmentPresenter,
  InstalledEnrollmentType,
} from 'types/enrollmentService';
import apolloClient from 'api/apolloClient';
import {
  ADD_ENROLLMENT_EXHIBITS,
  ADD_EXHIBIT,
  APPLY_POST_SUBMIT_USER_ACTION,
  APPLY_PROPOSAL_USER_ACTION,
  APPLY_USER_ACTION,
  BUILD_ENROLLMENT,
  CONFIRM_PROPOSAL,
  COPY_RFQ_PROPOSAL,
  DELETE_ENROLLMENT,
  DELETE_ENROLLMENTS,
  DELETE_PROPOSAL,
  DOWNLOAD_ALL_PROPOSAL_DOCUMENTS,
  DOWNLOAD_PROPOSAL_DOCUMENT,
  GET_ALL_PROPOSALS_FROM_ALL_ENROLLMENT_PERIODS_BY_GROUP_ID,
  GET_DOCUMENT_URL,
  GET_ENROLLMENT_PERIOD_BY_ID,
  GET_ENROLLMENTS_BY_GROUP_IDS,
  GET_INACTIVE_ENROLLMENT_PERIODS_BY_GROUP_ID,
  GET_PROPOSAL_CHECKLIST,
  INITIATE_PROPOSAL,
  MARK_AS_PREFERRED,
  PICK_CARRIERS,
  REMOVE_ENROLLMENT_EXHIBITS,
  REMOVE_PROPOSAL_DOCUMENTS,
  ROLLBACK_ENROLLMENT,
  SAVE_PROPOSAL,
  SAVE_PROPOSAL_AND_COMPLETE_STEP,
  SAVE_PROPOSAL_CHOICES,
  SELL_ENROLLMENT,
  SEND_RENEWAL_EMAILS,
  SHARE_IMQ,
  SUBMIT_INITIATE_RFQ,
  TRIGGER_ALL_RENEWAL,
  TRIGGER_RENEWAL,
  TRIGGER_RENEWALS_BATCH,
  UPDATE_EFFECTIVE_DATE,
  UPDATE_INITIATE_FORM_ANSWERS,
  UPDATE_PROPOSAL_NAME,
} from 'api/queries/enrollmentService';
import { ApolloQueryResult } from '@apollo/client';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { FetchResult } from 'apollo-link';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';

export async function requestApplyUserAction(periodId: string, userAction: USER_ACTIONS): Promise<void> {
  await apolloClient.mutate({
    mutation: APPLY_USER_ACTION,
    variables: { periodId, userAction },
  });
}

export async function requestApplyPostSubmitUserAction(periodId: string, userAction: USER_ACTIONS): Promise<void> {
  await apolloClient.mutate({
    mutation: APPLY_POST_SUBMIT_USER_ACTION,
    variables: { periodId, userAction },
  });
}

export async function getProposalChecklist(
  periodId: string,
  cartId: string,
): Promise<ApolloQueryResult<{ getProposalChecklist: RFQChecklist }>> {
  return apolloClient.query<{ getProposalChecklist: RFQChecklist }>({
    query: GET_PROPOSAL_CHECKLIST,
    fetchPolicy: 'no-cache',
    variables: { periodId, cartId },
  });
}

export async function getEnrollmentPeriod(periodId: string): Promise<EnrollmentPeriod> {
  return (
    await apolloClient.query<{ getEnrollmentPeriod: EnrollmentPeriod }>({
      query: GET_ENROLLMENT_PERIOD_BY_ID,
      fetchPolicy: 'no-cache',
      variables: { periodId },
    })
  ).data.getEnrollmentPeriod;
}

export async function deleteProposal(periodId: string, cartId: string): Promise<EnrollmentPeriod> {
  return (
    await apolloClient.mutate<{ getEnrollmentPeriod: EnrollmentPeriod }>({
      mutation: DELETE_PROPOSAL,
      variables: { periodId, cartId },
    })
  ).data!.getEnrollmentPeriod;
}

export async function completeEnrollment(
  periodId: string,
  submittedByUserName: string,
  finalBC: FinalBrowsingCriteria,
): Promise<EnrollmentPeriod> {
  return (
    await apolloClient.mutate<{ completeEnrollment: EnrollmentPeriod }>({
      mutation: BUILD_ENROLLMENT,
      variables: { periodId, submittedByUserName, finalBC },
    })
  ).data!.completeEnrollment;
}

/**
 * Method returns all (active and installed) enrollment periods and skipped inactive (Archived)
 * @param groupId
 */
export async function getAllEnrollmentPeriodsByGroupId(groupId: string): Promise<EnrollmentPeriod[]> {
  const res = await apolloClient.query<{ getAllEnrollmentPeriodsByGroupId: EnrollmentPeriod[] }>({
    query: GET_ALL_PROPOSALS_FROM_ALL_ENROLLMENT_PERIODS_BY_GROUP_ID,
    fetchPolicy: 'no-cache',
    variables: { groupId, showInstalled: true },
  });
  return res.data.getAllEnrollmentPeriodsByGroupId;
}

export async function getEnrollmentPeriodsByGroupIds(
  groupIds: string[],
  showInstalled: boolean,
): Promise<DashboardEnrollmentPresenter[]> {
  const res = await apolloClient.query<{ getEnrollmentsByGroupIds: DashboardEnrollmentPresenter[] }>({
    query: GET_ENROLLMENTS_BY_GROUP_IDS,
    fetchPolicy: 'no-cache',
    variables: { groupIds, showInstalled },
  });
  return res.data.getEnrollmentsByGroupIds;
}

export async function getInstalledEnrollmentPeriodsByGroupIds(groupIds: string[]): Promise<InstalledEnrollmentType[]> {
  const res = await apolloClient.query<{ getEnrollmentsByGroupIds: InstalledEnrollmentType[] }>({
    query: GET_ENROLLMENTS_BY_GROUP_IDS,
    fetchPolicy: 'no-cache',
    variables: { groupIds, showInstalled: true },
  });
  return res.data.getEnrollmentsByGroupIds;
}

export async function getAllInactiveEnrollmentPeriodsByGroupId(groupId: string): Promise<EnrollmentPeriod[]> {
  const res = await apolloClient.query<{ getInactiveEnrollmentsByGroupId: EnrollmentPeriod[] }>({
    query: GET_INACTIVE_ENROLLMENT_PERIODS_BY_GROUP_ID,
    fetchPolicy: 'no-cache',
    variables: { groupId },
  });
  return res.data.getInactiveEnrollmentsByGroupId;
}

export async function getDocumentPreSignUrl(
  periodId: string,
  cartId: string,
  fileName: string,
  fileType: string,
): Promise<ApolloQueryResult<{ getDocumentUrl: string }>> {
  return apolloClient.query({
    query: GET_DOCUMENT_URL,
    fetchPolicy: 'no-cache',
    variables: { periodId, cartId, fileName, fileType },
  });
}

export async function saveProposal(
  saveProposalInput: SaveProposalInput,
  rateSegment?: RateSegment,
  sponsorId?: string,
): Promise<FetchResult<{ saveProposal: EnrollmentPeriod }>> {
  return apolloClient.mutate({
    mutation: SAVE_PROPOSAL,
    variables: {
      proposal: saveProposalInput,
      rateSegment,
      sponsorId,
    },
  });
}

export async function initiateProposal(
  proposal: SaveProposalInput,
  rateSegment: RateSegment,
  initiateType: InitiateType,
  sponsorId?: string,
): Promise<FetchResult<{ initiateProposal: EnrollmentPeriod }>> {
  return apolloClient.mutate({
    mutation: INITIATE_PROPOSAL,
    variables: {
      proposal,
      rateSegment,
      sponsorId,
      initiateType,
    },
  });
}

export async function saveProposalAndCompleteStep(
  saveProposalInput: SaveProposalInput,
  periodId: string,
  userAction: USER_ACTIONS,
  rateSegment?: RateSegment,
  sponsorId?: string,
): Promise<EnrollmentPeriod> {
  const res = await apolloClient.mutate<{
    saveProposal: EnrollmentPeriod;
    applyProposalUserAction: EnrollmentPeriod;
  }>({
    mutation: SAVE_PROPOSAL_AND_COMPLETE_STEP,
    variables: {
      proposal: saveProposalInput,
      rateSegment,
      sponsorId,
      periodId,
      cartId: saveProposalInput.cartId,
      userAction,
    },
  });

  return res.data!.saveProposal;
}

export async function confirmCartItems(
  periodId: string,
  proposal: InputProposalV2,
  proposalAction: ProposalAction,
): Promise<void> {
  await apolloClient.mutate({
    mutation: CONFIRM_PROPOSAL,
    variables: { periodId, proposal, proposalAction },
  });
}

export async function saveProposalChoices(periodId: string, proposal: InputProposalV2): Promise<void> {
  await apolloClient.mutate({
    mutation: SAVE_PROPOSAL_CHOICES,
    variables: { periodId, proposal },
  });
}

export async function markAsPreferred(periodId: string, cartId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: MARK_AS_PREFERRED,
    variables: { periodId, cartId },
  });
}

export async function updateInitiateFormAnswers(
  periodId: string,
  cartId: string,
  formAnswers: Record<string, string>,
): Promise<void> {
  await apolloClient.mutate({
    mutation: UPDATE_INITIATE_FORM_ANSWERS,
    variables: { periodId, cartId, formAnswers },
  });
}

export async function rollbackEnrollment(periodId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: ROLLBACK_ENROLLMENT,
    variables: { periodId },
  });
}

export async function sellEnrollment(periodId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: SELL_ENROLLMENT,
    variables: { periodId },
  });
}

export async function updateProposalName(periodId: string, cartId: string, name: string): Promise<void> {
  await apolloClient.mutate({
    mutation: UPDATE_PROPOSAL_NAME,
    variables: { periodId, cartId, name },
  });
}

export async function triggerRenewal(
  periodId: string,
  emailReceivers: EmailReceiver[],
  sendEmails = true,
): Promise<EnrollmentPeriod> {
  return (
    await apolloClient.mutate<{ triggerRenewal: EnrollmentPeriod }>({
      mutation: TRIGGER_RENEWAL,
      variables: { periodId, emailReceivers, sendEmails },
    })
  ).data!.triggerRenewal;
}

export async function deleteEnrollment(periodId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: DELETE_ENROLLMENT,
    variables: { periodId },
  });
}

export async function deleteEnrollments(periodIds: string[]): Promise<void> {
  await apolloClient.mutate({
    mutation: DELETE_ENROLLMENTS,
    variables: { periodIds },
  });
}

export async function downloadProposalDocument(
  periodId: string,
  cartId: string,
  documentId: string,
): Promise<FetchResult<{ downloadProposalDocument: string }>> {
  return apolloClient.mutate({
    mutation: DOWNLOAD_PROPOSAL_DOCUMENT,
    variables: { periodId, cartId, documentId },
  });
}

export async function downloadAllProposalDocuments(
  periodId: string,
  cartId: string,
): Promise<FetchResult<{ downloadAllProposalDocuments: string }>> {
  return apolloClient.mutate({
    mutation: DOWNLOAD_ALL_PROPOSAL_DOCUMENTS,
    variables: { periodId, cartId },
  });
}

export async function applyProposalUserAction(
  periodId: string,
  cartId: string,
  userAction: USER_ACTIONS,
): Promise<FetchResult<{ applyProposalUserAction: EnrollmentPeriod }>> {
  return apolloClient.mutate({
    mutation: APPLY_PROPOSAL_USER_ACTION,
    variables: { periodId, cartId, userAction },
  });
}

export async function copyRFQProposal(
  periodId: string,
  cartId: string,
  newCartId: string,
): Promise<FetchResult<{ copyRFQProposal: EnrollmentPeriod }>> {
  return apolloClient.mutate({
    mutation: COPY_RFQ_PROPOSAL,
    variables: { periodId, cartId, newCartId },
  });
}

export async function removeProposalDocuments(periodId: string, cartId: string, documentId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: REMOVE_PROPOSAL_DOCUMENTS,
    variables: { periodId, cartId, documentId },
  });
}

export async function triggerAllRenewal(
  sponsorId: string,
  effectiveDate: string,
  sendEmails: boolean,
): Promise<FetchResult<{ triggerAllRenewal: boolean }>> {
  return apolloClient.mutate<{ triggerAllRenewal: boolean }>({
    mutation: TRIGGER_ALL_RENEWAL,
    variables: { sponsorId, effectiveDate, sendEmails },
  });
}

export async function triggerRenewalsBatch(periodIds: string[], sendEmails?: boolean): Promise<boolean> {
  return (
    await apolloClient.mutate<{ triggerRenewalsBatch: boolean }>({
      mutation: TRIGGER_RENEWALS_BATCH,
      variables: { periodIds, sendEmails },
    })
  ).data!.triggerRenewalsBatch;
}

export async function addExhibit(
  periodId: string,
  productLine: ProductLineId,
  fileUrl: string,
): Promise<FetchResult<{ addExhibit: boolean }>> {
  return apolloClient.mutate<{ addExhibit: boolean }>({
    mutation: ADD_EXHIBIT,
    variables: { periodId, productLine, fileUrl },
  });
}

export async function addEnrollmentExhibits(
  periodId: string,
  productLineId: string,
  fileUrls: string[],
): Promise<InstalledEnrollmentType> {
  const res = await apolloClient.mutate<{ addEnrollmentExhibits: InstalledEnrollmentType }>({
    mutation: ADD_ENROLLMENT_EXHIBITS,
    variables: { periodId, productLineId, fileUrls },
  });
  return res.data!.addEnrollmentExhibits;
}

export async function removeEnrollmentExhibits(
  periodId: string,
  productLineId: string,
  fileUrls: string[],
): Promise<InstalledEnrollmentType> {
  const res = await apolloClient.mutate<{ removeEnrollmentExhibits: InstalledEnrollmentType }>({
    mutation: REMOVE_ENROLLMENT_EXHIBITS,
    variables: { periodId, productLineId, fileUrls },
  });
  return res.data!.removeEnrollmentExhibits;
}

export async function updateEffectiveDate(periodId: string, effectiveDate: string): Promise<InstalledEnrollmentType> {
  const res = await apolloClient.mutate<{ updateEffectiveDate: InstalledEnrollmentType }>({
    mutation: UPDATE_EFFECTIVE_DATE,
    variables: { periodId, effectiveDate },
  });
  return res.data!.updateEffectiveDate;
}

export async function submitInitiateRFQ(
  periodId: string,
  cartId: string,
  recipients: string[],
): Promise<FetchResult<{ submitInitiateRFQ: EnrollmentPeriod }>> {
  return apolloClient.mutate<{ submitInitiateRFQ: EnrollmentPeriod }>({
    mutation: SUBMIT_INITIATE_RFQ,
    variables: { periodId, cartId, recipients },
  });
}

export async function pickCarriers(
  periodId: string,
  cartId: string,
  productLines: InputRFQCarrierByPLine[],
): Promise<FetchResult<{ pickCarriers: EnrollmentPeriod }>> {
  return apolloClient.mutate<{ pickCarriers: EnrollmentPeriod }>({
    mutation: PICK_CARRIERS,
    variables: { periodId, cartId, productLines },
  });
}

export async function shareIMQ(periodId: string, cartId: string, recipients: string[]): Promise<void> {
  await apolloClient.mutate({
    mutation: SHARE_IMQ,
    variables: { periodId, cartId, recipients },
  });
}

export async function sendRenewalEmails(
  periodId: string,
  emailReceivers: EmailReceiver[],
): Promise<FetchResult<{ sendRenewalEmails: boolean }>> {
  return apolloClient.mutate<{ sendRenewalEmails: boolean }>({
    mutation: SEND_RENEWAL_EMAILS,
    variables: { periodId, emailReceivers },
  });
}
